import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { VideoPlayer } from '@/components/VideoPlayer';
import { 
  ArrowLeft, Clock, Download, Check, Lock, Play,
  ChevronRight, Video as VideoIcon
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const VideoWatch = () => {
  const { courseId, videoId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [watchProgress, setWatchProgress] = useState({});

  useEffect(() => {
    checkAccessAndLoad();
  }, [courseId, videoId]);

  const checkAccessAndLoad = async () => {
    try {
      // Get course details
      const courseRes = await axios.get(`${API}/video-courses/${courseId}`);
      setCourse(courseRes.data);

      // Check if user has purchased this course (admins bypass all gates)
      const authUser = localStorage.getItem('auth_user');
      const isAdmin = authUser ? (JSON.parse(authUser)?.role === 'admin') : false;
      if (isAdmin) {
        setHasAccess(true);
      } else {
        const email = localStorage.getItem('purchase_email');
        if (email) {
          const purchasesRes = await axios.get(`${API}/purchases/${email}`);
          const hasPurchased = purchasesRes.data.some(
            p => p.items?.some(item => item.item_id === courseId && item.item_type === 'video_course')
          );
          setHasAccess(hasPurchased);
        }
      }

      // Load saved progress
      const savedProgress = localStorage.getItem(`video_progress_${courseId}`);
      if (savedProgress) {
        setWatchProgress(JSON.parse(savedProgress));
      }

      // Set current video
      if (videoId && courseRes.data.videos) {
        const video = courseRes.data.videos.find(v => v.id === videoId);
        setCurrentVideo(video);
      } else if (courseRes.data.videos?.length > 0) {
        setCurrentVideo(courseRes.data.videos[0]);
      }

    } catch (error) {
      console.error('Failed to load course:', error);
      toast.error('Failed to load video');
    } finally {
      setLoading(false);
    }
  };

  const handleProgress = (currentTime, duration) => {
    if (!currentVideo) return;
    
    const newProgress = {
      ...watchProgress,
      [currentVideo.id]: {
        currentTime,
        duration,
        percentage: (currentTime / duration) * 100,
        lastWatched: new Date().toISOString()
      }
    };
    setWatchProgress(newProgress);
    localStorage.setItem(`video_progress_${courseId}`, JSON.stringify(newProgress));
  };

  const handleVideoComplete = () => {
    // Mark as complete
    const newProgress = {
      ...watchProgress,
      [currentVideo.id]: {
        ...watchProgress[currentVideo.id],
        completed: true,
        percentage: 100
      }
    };
    setWatchProgress(newProgress);
    localStorage.setItem(`video_progress_${courseId}`, JSON.stringify(newProgress));

    // Auto-play next video
    if (course?.videos) {
      const currentIndex = course.videos.findIndex(v => v.id === currentVideo.id);
      if (currentIndex < course.videos.length - 1) {
        const nextVideo = course.videos[currentIndex + 1];
        toast.success('Playing next video...');
        setTimeout(() => {
          setCurrentVideo(nextVideo);
          navigate(`/watch/${courseId}/${nextVideo.id}`, { replace: true });
        }, 2000);
      } else {
        toast.success('Course complete! 🎉');
      }
    }
  };

  const selectVideo = (video) => {
    setCurrentVideo(video);
    navigate(`/watch/${courseId}/${video.id}`, { replace: true });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getVideoProgress = (videoId) => {
    return watchProgress[videoId]?.percentage || 0;
  };

  const isVideoCompleted = (videoId) => {
    return watchProgress[videoId]?.completed || watchProgress[videoId]?.percentage >= 90;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12 text-center">
        <h1 className="text-2xl font-bold text-foreground mb-4">Course not found</h1>
        <Link to="/videos" className="text-primary hover:underline">
          Browse video courses
        </Link>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-card border border-border rounded-2xl p-8 text-center">
          <Lock className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Purchase Required</h1>
          <p className="text-muted-foreground mb-6">
            You need to purchase this course to watch the videos.
          </p>
          <div className="flex gap-4 justify-center">
            <Link
              to="/videos"
              className="px-6 py-3 bg-card border border-border rounded-full font-bold text-foreground hover:bg-background transition-colors"
            >
              Browse Courses
            </Link>
            <Link
              to={`/videos`}
              className="px-6 py-3 bg-primary hover:bg-primary/90 rounded-full font-bold text-primary-foreground transition-colors"
            >
              Purchase for ${course.price?.toFixed(2)}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/library')}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-foreground">{course.title}</h1>
              <p className="text-sm text-muted-foreground">
                {course.videos?.length || 0} videos • {course.instructor}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Video Player */}
          <div className="lg:col-span-2">
            {currentVideo ? (
              <motion.div
                key={currentVideo.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <VideoPlayer
                  src={currentVideo.video_url}
                  title={currentVideo.title}
                  poster={currentVideo.thumbnail_url || course.thumbnail_url}
                  downloadUrl={currentVideo.video_url}
                  initialProgress={watchProgress[currentVideo.id]?.currentTime || 0}
                  onProgress={handleProgress}
                  onEnded={handleVideoComplete}
                />
                
                {/* Video Info */}
                <div className="mt-6">
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    {currentVideo.title}
                  </h2>
                  {currentVideo.description && (
                    <p className="text-muted-foreground">
                      {currentVideo.description}
                    </p>
                  )}
                  
                  {/* Download Button */}
                  <a
                    href={currentVideo.video_url}
                    download
                    className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-card border border-border rounded-full font-medium text-foreground hover:bg-background transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download Video
                  </a>
                </div>
              </motion.div>
            ) : (
              <div className="aspect-video bg-card rounded-2xl flex items-center justify-center">
                <p className="text-muted-foreground">Select a video to start watching</p>
              </div>
            )}
          </div>

          {/* Video List */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-border rounded-2xl overflow-hidden sticky top-24">
              <div className="p-4 border-b border-border">
                <h3 className="font-bold text-foreground">Course Content</h3>
                <p className="text-sm text-muted-foreground">
                  {Object.values(watchProgress).filter(p => p.completed || p.percentage >= 90).length} / {course.videos?.length || 0} completed
                </p>
              </div>
              
              <div className="max-h-[60vh] overflow-y-auto">
                {course.videos?.map((video, index) => {
                  const isActive = currentVideo?.id === video.id;
                  const completed = isVideoCompleted(video.id);
                  const progress = getVideoProgress(video.id);
                  
                  return (
                    <button
                      key={video.id}
                      onClick={() => selectVideo(video)}
                      className={`w-full flex items-start gap-3 p-4 text-left transition-colors border-b border-border/50 last:border-0 ${
                        isActive 
                          ? 'bg-primary/10' 
                          : 'hover:bg-background'
                      }`}
                    >
                      {/* Number/Status */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        completed 
                          ? 'bg-green-500/20 text-green-500'
                          : isActive 
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-background text-muted-foreground'
                      }`}>
                        {completed ? (
                          <Check className="w-4 h-4" />
                        ) : isActive ? (
                          <Play className="w-4 h-4" fill="currentColor" />
                        ) : (
                          <span className="text-sm font-medium">{index + 1}</span>
                        )}
                      </div>
                      
                      {/* Video Info */}
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium truncate ${
                          isActive ? 'text-primary' : 'text-foreground'
                        }`}>
                          {video.title}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                          <Clock className="w-3 h-3" />
                          {formatDuration(video.duration)}
                        </div>
                        
                        {/* Progress Bar */}
                        {progress > 0 && progress < 100 && (
                          <div className="mt-2 h-1 bg-background rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-primary rounded-full"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        )}
                      </div>
                      
                      <ChevronRight className={`w-5 h-5 flex-shrink-0 ${
                        isActive ? 'text-primary' : 'text-muted-foreground'
                      }`} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
